import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { isSelfOnlyPrivateCapsule } from "../utils/messageClassification";
import { isGooglePlaceMediaUrl } from "../utils/seededPlaceMedia";
import { isVideoMediaType, isLikelyVideoUrl } from "../utils/mediaUrl";

const OPENED_CAPSULE_TYPES = new Set(["public", "sponsored", "memberexclusive"]);
export const QUICK_FIND_LABELS = {
  eat: "Food nearby",
  drink: "Drinks nearby",
  fun: "Fun nearby",
};
const QUICK_FIND_KEYS = new Set(Object.keys(QUICK_FIND_LABELS));

export function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const asDate = new Date(value);
  return Number.isNaN(asDate.getTime()) ? 0 : asDate.getTime();
}

function isFirebaseStorageUrl(url = "") {
  try {
    const parsed = new URL(String(url || "").trim());
    return String(parsed.hostname || "").toLowerCase() === "firebasestorage.googleapis.com";
  } catch {
    return false;
  }
}

function cleanHistoryThumbnailUrl(url = "") {
  const cleanUrl = String(url || "").trim();
  return cleanUrl && !isGooglePlaceMediaUrl(cleanUrl) && !isLikelyVideoUrl(cleanUrl) ? cleanUrl : "";
}

function uniqueCleanIds(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    )
  );
}

function capsuleIdsFromFindItem(item = {}) {
  return uniqueCleanIds(
    Array.isArray(item?.capsuleIds)
      ? item.capsuleIds
      : [item?.capsuleId]
  );
}

function normalizeQuickFindCategoryKey(value = "") {
  const key = String(value || "").trim().toLowerCase();
  return QUICK_FIND_KEYS.has(key) ? key : "";
}

function quickFindPromptForCategory(categoryKey = "") {
  return QUICK_FIND_LABELS[normalizeQuickFindCategoryKey(categoryKey)] || "";
}

function normalizeFindKeyText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeFindCenter(center = null) {
  const lat = Number(center?.lat);
  const lng = Number(center?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function findAreaKey(item = {}) {
  const placeId = String(item?.placeId || item?.searchPlaceId || "").trim();
  if (placeId) return `place:${placeId}`;

  const center = normalizeFindCenter(item?.center || item?.searchCenter);
  if (center) return `geo:${center.lat.toFixed(3)},${center.lng.toFixed(3)}`;

  const locationLabel = normalizeFindKeyText(item?.locationLabel);
  return locationLabel ? `label:${locationLabel}` : "area:anywhere";
}

export function buildFindHistoryDedupeKey(item = {}) {
  const quickFindCategoryKey = normalizeQuickFindCategoryKey(item?.quickFindCategoryKey);
  const prompt = normalizeFindKeyText(item?.prompt || item?.title || quickFindPromptForCategory(quickFindCategoryKey));
  const intentKey = quickFindCategoryKey
    ? `quick:${quickFindCategoryKey}`
    : prompt
      ? `prompt:${prompt}`
      : "";
  if (!intentKey) return "";
  return `${intentKey}|${findAreaKey(item)}`;
}

function stableHash(value = "") {
  let hash = 5381;
  const text = String(value || "");
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) + hash) ^ text.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

function stableFindHistoryDocId(item = {}) {
  const key = buildFindHistoryDedupeKey(item);
  return `find_${stableHash(key || JSON.stringify(item || {}))}`;
}

export function shouldHydrateFindHistoryPreview(item = {}) {
  return (
    !cleanHistoryThumbnailUrl(item?.thumbnailUrl) ||
    !String(item?.locationLabel || "").trim()
  );
}

function mediaItemUrl(item = {}) {
  const previewUrl = String(
    item?.sessionPreviewUrl ||
      item?.posterUrl ||
      item?.thumbnailUrl ||
      item?.imageUrl ||
      item?.photoUrl ||
      item?.photoURL ||
      ""
  ).trim();
  if (previewUrl) return previewUrl;
  if (isVideoMediaType(item?.type || item?.contentType || item?.mimeType)) return "";
  return String(
    item?.downloadURL ||
      item?.url ||
      ""
  ).trim();
}

function firstMediaUrl(capsule = {}) {
  const directCandidates = [
    capsule.thumbnailUrl,
    capsule.imageUrl,
    capsule.imageURL,
    capsule.photoUrl,
    capsule.photoURL,
    capsule.previewImageUrl,
    capsule.heroImageUrl,
    capsule.coverImageUrl,
    capsule.resultImageUrl,
    capsule.placeImageUrl,
    capsule.placePhotoUrl,
  ]
    .map(cleanHistoryThumbnailUrl)
    .filter(Boolean);
  if (directCandidates.length) return directCandidates[0];

  const media = [
    ...(Array.isArray(capsule.media) ? capsule.media : []),
    ...(Array.isArray(capsule.mediaFiles) ? capsule.mediaFiles : []),
    ...(Array.isArray(capsule.previewMedia) ? capsule.previewMedia : []),
    ...(Array.isArray(capsule.photoUrls) ? capsule.photoUrls.map((url) => ({ url })) : []),
    ...(Array.isArray(capsule.photoURLs) ? capsule.photoURLs.map((url) => ({ url })) : []),
    ...(Array.isArray(capsule.images) ? capsule.images : []),
    ...(Array.isArray(capsule.photos) ? capsule.photos : []),
  ];
  const candidates = media
    .map((entry) => ({
      entry,
      url: cleanHistoryThumbnailUrl(mediaItemUrl(entry)),
    }))
    .filter((candidate) => candidate.url);
  const storageCandidate = candidates.find(
    ({ entry, url }) =>
      String(entry?.storagePath || entry?.sourceStoragePath || "").trim() ||
      isFirebaseStorageUrl(url)
  );
  return storageCandidate?.url || candidates[0]?.url || "";
}

function titleFromCapsule(capsule = {}, fallback = "Time capsule") {
  return String(
    capsule.messageTitle ||
      capsule.subject ||
      capsule.title ||
      capsule.placeName ||
      capsule.location?.placeName ||
      fallback
  ).trim();
}

function locationLabelFromCapsule(capsule = {}) {
  return String(capsule.locationLabel || capsule.address || capsule.location?.address || capsule.location?.placeName || "").trim();
}

export function hydrateFindGrantPreviews(findGrants = [], capsules = []) {
  const capsuleById = new Map(
    (Array.isArray(capsules) ? capsules : [])
      .filter((capsule) => capsule?.id)
      .map((capsule) => [String(capsule.id), capsule])
  );

  return (Array.isArray(findGrants) ? findGrants : []).map((grant) => {
    const capsuleIds = capsuleIdsFromFindItem(grant).filter((id) => capsuleById.has(id));
    if (!capsuleIds.length) return null;
    const capsulesForGrant = capsuleIds.map((id) => capsuleById.get(id)).filter(Boolean);
    const mediaCapsule = capsulesForGrant.find((capsule) => firstMediaUrl(capsule));
    const locationCapsule = mediaCapsule || capsulesForGrant.find((capsule) => locationLabelFromCapsule(capsule));

    return {
      ...grant,
      capsuleIds,
      thumbnailUrl: cleanHistoryThumbnailUrl(grant?.thumbnailUrl) || firstMediaUrl(mediaCapsule),
      locationLabel: String(grant?.locationLabel || locationLabelFromCapsule(locationCapsule) || "").trim(),
    };
  }).filter(Boolean);
}

function baseCapsuleItem({ id, capsule, filterType, badge, lastActiveMs, lastActiveAt }) {
  const capsuleId = String(capsule?.id || id || "").trim();
  return {
    id,
    type: filterType === "clicked" ? "capsule_opened" : "capsule",
    filterType,
    capsuleId,
    title: titleFromCapsule(capsule),
    subtitle: locationLabelFromCapsule(capsule),
    thumbnailUrl: firstMediaUrl(capsule),
    badge,
    capsuleType: String(capsule?.capsuleType || "").trim(),
    lastActiveAt: lastActiveAt || capsule?.lastActiveAt || capsule?.sentAt || capsule?.createdAt || null,
    lastActiveMs,
    href: capsuleId ? `/capsule/${encodeURIComponent(capsuleId)}` : "",
  };
}

function mapExplicitHistoryItem(item = {}) {
  const capsuleId = String(item.capsuleId || "").trim();
  const lastActiveMs = timestampToMillis(item.lastActiveAt) || timestampToMillis(item.updatedAt) || timestampToMillis(item.createdAt);
  if (item.type === "find") {
    const capsuleIds = capsuleIdsFromFindItem(item);
    if (!capsuleIds.length) return null;
    const quickFindCategoryKey = normalizeQuickFindCategoryKey(item.quickFindCategoryKey);
    const prompt = String(item.prompt || item.title || quickFindPromptForCategory(quickFindCategoryKey) || "Find").trim();
    const locationLabel = String(item.locationLabel || "").trim();
    return {
      id: String(item.id || `find-${lastActiveMs || prompt}`).trim(),
      type: "find",
      filterType: "find",
      title: prompt,
      subtitle: locationLabel || (capsuleIds.length ? `${capsuleIds.length} result${capsuleIds.length === 1 ? "" : "s"}` : ""),
      locationLabel,
      thumbnailUrl: cleanHistoryThumbnailUrl(item.thumbnailUrl),
      badge: "Find",
      prompt,
      quickFindCategoryKey,
      source: String(item.source || "").trim(),
      placeId: String(item.placeId || "").trim(),
      center: item.center || null,
      historyDedupeKey: buildFindHistoryDedupeKey({ ...item, prompt, quickFindCategoryKey }),
      capsuleIds,
      lastActiveAt: item.lastActiveAt || item.updatedAt || item.createdAt || null,
      lastActiveMs,
      href: "/",
    };
  }

  return {
    id: String(item.id || `opened-${capsuleId}`).trim(),
    type: "capsule_opened",
    filterType: "clicked",
    capsuleId,
    title: String(item.title || "Time capsule").trim(),
    subtitle: String(item.locationLabel || "").trim(),
    thumbnailUrl: cleanHistoryThumbnailUrl(item.thumbnailUrl),
    badge: "Opened",
    capsuleType: String(item.capsuleType || "").trim(),
    lastActiveAt: item.lastActiveAt || item.updatedAt || item.createdAt || null,
    lastActiveMs,
    href: capsuleId ? `/capsule/${encodeURIComponent(capsuleId)}` : "",
  };
}

function mapFindGrant(grant = {}) {
  const capsuleIds = capsuleIdsFromFindItem(grant);
  if (!capsuleIds.length) return null;
  const lastActiveAt = grant.lastActiveAt || grant.updatedAt || grant.createdAt || null;
  const lastActiveMs = timestampToMillis(lastActiveAt);
  const quickFindCategoryKey = normalizeQuickFindCategoryKey(grant.quickFindCategoryKey);
  const prompt = String(grant.prompt || quickFindPromptForCategory(quickFindCategoryKey) || "Find").trim();
  const locationLabel = String(grant.locationLabel || "").trim();
  return {
    id: String(grant.id || `find-${capsuleIds.join("-") || lastActiveMs}`).trim(),
    type: "find",
    filterType: "find",
    title: prompt,
    subtitle: locationLabel || (capsuleIds.length ? `${capsuleIds.length} result${capsuleIds.length === 1 ? "" : "s"}` : ""),
    locationLabel,
    thumbnailUrl: cleanHistoryThumbnailUrl(grant.thumbnailUrl),
    badge: "Find",
    prompt,
    quickFindCategoryKey,
    source: String(grant.source || "").trim(),
    placeId: String(grant.placeId || "").trim(),
    center: grant.center || null,
    historyDedupeKey: buildFindHistoryDedupeKey({ ...grant, prompt, quickFindCategoryKey }),
    capsuleIds,
    lastActiveAt,
    lastActiveMs,
    href: "/",
  };
}

function mapReceivedCapsule(capsule = {}, uid = "") {
  const readBy = Array.isArray(capsule.readBy) ? capsule.readBy : [];
  const revealMs = timestampToMillis(capsule.revealDate);
  const isLocked = revealMs > Date.now();
  const isRead = uid && readBy.includes(uid);
  const lastActiveAt = capsule.lastActiveAt || capsule.sentAt || capsule.createdAt || null;
  return baseCapsuleItem({
    id: `received-${capsule.id}`,
    capsule,
    filterType: "received",
    badge: isLocked ? "Locked" : isRead ? "Received" : "Unread",
    lastActiveAt,
    lastActiveMs: timestampToMillis(lastActiveAt),
  });
}

function mapPersonalCapsule(capsule = {}) {
  const lastActiveAt = capsule.lastActiveAt || capsule.sentAt || capsule.createdAt || null;
  return baseCapsuleItem({
    id: `personal-${capsule.id}`,
    capsule,
    filterType: "personal",
    badge: "Memories",
    lastActiveAt,
    lastActiveMs: timestampToMillis(lastActiveAt),
  });
}

export function sortHistoryItems(items = [], sort = "recent") {
  const direction = sort === "oldest" ? 1 : -1;
  return [...items].sort((a, b) => {
    const delta = (Number(a.lastActiveMs) || 0) - (Number(b.lastActiveMs) || 0);
    if (delta !== 0) return delta * direction;
    return String(a.title || "").localeCompare(String(b.title || ""));
  });
}

export function filterHistoryItems(items = [], filter = "all") {
  if (!filter || filter === "all") return items;
  return items.filter((item) => item.filterType === filter);
}

function mergeFindHistoryItems(existing, next) {
  if (!existing) {
    return {
      ...next,
      capsuleIds: uniqueCleanIds(next?.capsuleIds),
    };
  }
  const existingMs = Number(existing.lastActiveMs) || 0;
  const nextMs = Number(next.lastActiveMs) || 0;
  const base = nextMs >= existingMs ? next : existing;
  const fallback = base === next ? existing : next;
  const capsuleIds = uniqueCleanIds([...(base.capsuleIds || []), ...(fallback.capsuleIds || [])]);
  return {
    ...base,
    capsuleIds,
    thumbnailUrl: base.thumbnailUrl || fallback.thumbnailUrl || "",
    locationLabel: base.locationLabel || fallback.locationLabel || "",
    subtitle:
      base.locationLabel ||
      fallback.locationLabel ||
      (capsuleIds.length ? `${capsuleIds.length} result${capsuleIds.length === 1 ? "" : "s"}` : ""),
    historyDedupeKey: base.historyDedupeKey || fallback.historyDedupeKey || "",
  };
}

function addHistoryItem({ byId, findByKey }, item) {
  if (!item?.id) return;
  if (item.type !== "find") {
    byId.set(item.id, item);
    return;
  }
  const key = item.historyDedupeKey || buildFindHistoryDedupeKey(item) || item.id;
  findByKey.set(key, mergeFindHistoryItems(findByKey.get(key), item));
}

function hiddenHistoryDocId(itemId = "") {
  return encodeURIComponent(String(itemId || "").trim()).replace(/\./g, "%2E");
}

export function filterHiddenHistoryItems(items = [], hiddenItemIds = []) {
  const hidden = new Set((Array.isArray(hiddenItemIds) ? hiddenItemIds : []).map((id) => String(id || "").trim()));
  if (!hidden.size) return items;
  return (Array.isArray(items) ? items : []).filter((item) => !hidden.has(String(item?.id || "").trim()));
}

export function getHiddenHistoryItems(items = [], hiddenItemIds = []) {
  const hidden = new Set((Array.isArray(hiddenItemIds) ? hiddenItemIds : []).map((id) => String(id || "").trim()));
  if (!hidden.size) return [];
  return (Array.isArray(items) ? items : []).filter((item) => hidden.has(String(item?.id || "").trim()));
}

export function buildHistoryItems({
  uid = "",
  findGrants = [],
  explicitHistory = [],
  receivedCapsules = [],
  personalCapsules = [],
} = {}) {
  const byId = new Map();
  const findByKey = new Map();

  explicitHistory.map(mapExplicitHistoryItem).forEach((item) => {
    addHistoryItem({ byId, findByKey }, item);
  });

  findGrants.map(mapFindGrant).forEach((item) => {
    addHistoryItem({ byId, findByKey }, item);
  });

  findByKey.forEach((item) => {
    if (item?.id) byId.set(item.id, item);
  });

  receivedCapsules.map((capsule) => mapReceivedCapsule(capsule, uid)).forEach((item) => {
    if (item.id) byId.set(item.id, item);
  });

  personalCapsules
    .filter((capsule) => isSelfOnlyPrivateCapsule(capsule, uid))
    .map(mapPersonalCapsule)
    .forEach((item) => {
      if (item.id) byId.set(item.id, item);
    });

  return sortHistoryItems(Array.from(byId.values()), "recent");
}

export function canRecordOpenedCapsule(capsule = {}, uid = "") {
  if (!uid || !capsule?.id) return false;
  const type = String(capsule.capsuleType || "").trim().toLowerCase();
  return OPENED_CAPSULE_TYPES.has(type);
}

export async function recordOpenedCapsuleHistory(uid, capsule = {}) {
  if (!canRecordOpenedCapsule(capsule, uid)) return false;
  const capsuleId = String(capsule.id || "").trim();
  const itemRef = doc(db, "userHistory", uid, "items", `capsule_${capsuleId}`);
  await setDoc(
    itemRef,
    {
      type: "capsule_opened",
      capsuleId,
      title: titleFromCapsule(capsule),
      thumbnailUrl: firstMediaUrl(capsule),
      capsuleType: String(capsule.capsuleType || "").trim(),
      locationLabel: locationLabelFromCapsule(capsule),
      lastActiveAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    },
    { merge: true }
  );
  return true;
}

export async function recordFindHistory(uid, {
  prompt = "",
  capsules = [],
  capsuleIds = [],
  center = null,
  quickFindCategoryKey = "",
  source = "",
  placeId = "",
  locationLabel = "",
} = {}) {
  const cleanUid = String(uid || "").trim();
  const resultCapsules = Array.isArray(capsules) ? capsules : [];
  const cleanQuickFindCategoryKey = normalizeQuickFindCategoryKey(quickFindCategoryKey);
  const cleanPrompt = String(prompt || quickFindPromptForCategory(cleanQuickFindCategoryKey) || "").trim();
  const resultCapsuleIds = uniqueCleanIds([
    ...(Array.isArray(capsuleIds) ? capsuleIds : []),
    ...resultCapsules.map((capsule) => capsule?.id || capsule?.capsuleId),
  ]);
  if (!cleanUid || !cleanPrompt || !resultCapsuleIds.length) return false;
  const resultIdSet = new Set(resultCapsuleIds);
  const firstCapsule =
    resultCapsules.find((capsule) => resultIdSet.has(String(capsule?.id || capsule?.capsuleId || "").trim()) && firstMediaUrl(capsule)) ||
    resultCapsules.find((capsule) => resultIdSet.has(String(capsule?.id || capsule?.capsuleId || "").trim())) ||
    {};
  const cleanPlaceId = String(placeId || "").trim();
  const normalizedCenter = normalizeFindCenter(center);
  const itemId = stableFindHistoryDocId({
    prompt: cleanPrompt,
    quickFindCategoryKey: cleanQuickFindCategoryKey,
    center: normalizedCenter,
    placeId: cleanPlaceId,
  });
  const payload = {
    type: "find",
    prompt: cleanPrompt,
    capsuleIds: resultCapsuleIds,
    thumbnailUrl: firstMediaUrl(firstCapsule),
    locationLabel: String(locationLabel || locationLabelFromCapsule(firstCapsule) || "").trim(),
    center: normalizedCenter,
    lastActiveAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (cleanQuickFindCategoryKey) payload.quickFindCategoryKey = cleanQuickFindCategoryKey;
  const cleanSource = String(source || "").trim();
  if (cleanSource) payload.source = cleanSource;
  if (cleanPlaceId) payload.placeId = cleanPlaceId;

  await setDoc(doc(db, "userHistory", cleanUid, "items", itemId), payload, { merge: true });
  return true;
}

function mapDoc(docSnap) {
  return { id: docSnap.id, ...(docSnap.data?.() || {}) };
}

export function subscribeUserHistoryItems(uid, onChange, onError) {
  if (!uid) {
    onChange([]);
    return () => {};
  }
  return onSnapshot(
    query(collection(db, "userHistory", uid, "items"), orderBy("lastActiveAt", "desc"), limit(100)),
    (snap) => onChange(snap.docs.map(mapDoc)),
    onError
  );
}

export function subscribeHiddenHistoryItemIds(uid, onChange, onError) {
  if (!uid) {
    onChange([]);
    return () => {};
  }
  return onSnapshot(
    query(collection(db, "userHistory", uid, "hidden"), limit(500)),
    (snap) => onChange(snap.docs.map((docSnap) => String(docSnap.data()?.itemId || docSnap.id || "").trim()).filter(Boolean)),
    onError
  );
}

export async function hideHistoryItem(uid, itemId) {
  const cleanUid = String(uid || "").trim();
  const cleanItemId = String(itemId || "").trim();
  if (!cleanUid || !cleanItemId) return false;
  await setDoc(doc(db, "userHistory", cleanUid, "hidden", hiddenHistoryDocId(cleanItemId)), {
    itemId: cleanItemId,
    hiddenAt: serverTimestamp(),
  });
  return true;
}

export async function restoreHiddenHistoryItem(uid, itemId) {
  const cleanUid = String(uid || "").trim();
  const cleanItemId = String(itemId || "").trim();
  if (!cleanUid || !cleanItemId) return false;
  await deleteDoc(doc(db, "userHistory", cleanUid, "hidden", hiddenHistoryDocId(cleanItemId)));
  return true;
}

export async function fetchAskDiscoveryGrantItems(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, "askDiscoveryGrants"), where("uid", "==", uid)));
  const grouped = new Map();
  snap.docs.map(mapDoc).forEach((grant) => {
    const prompt = String(grant.prompt || "").trim();
    const key = buildFindHistoryDedupeKey(grant) || prompt || grant.id;
    const capsuleId = String(grant.capsuleId || "").trim();
    const existing = grouped.get(key) || { ...grant, capsuleIds: [] };
    const mergedCapsuleIds = uniqueCleanIds([...(existing.capsuleIds || []), capsuleId]);
    const currentMs = timestampToMillis(existing.updatedAt || existing.createdAt);
    const nextMs = timestampToMillis(grant.updatedAt || grant.createdAt);
    if (nextMs >= currentMs) {
      grouped.set(key, {
        ...existing,
        ...grant,
        capsuleIds: mergedCapsuleIds,
        updatedAt: grant.updatedAt,
        createdAt: grant.createdAt,
      });
      return;
    }
    grouped.set(key, { ...existing, capsuleIds: mergedCapsuleIds });
  });
  return Array.from(grouped.values()).filter((grant) => Array.isArray(grant.capsuleIds) && grant.capsuleIds.length);
}
